import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { dashboardAPI } from '../../services/api';

export const fetchStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getStats();
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch stats';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchApiLogs = createAsyncThunk(
  'dashboard/fetchApiLogs',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getApiLogs(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch API logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchLuxSciContacts = createAsyncThunk(
  'dashboard/fetchLuxSciContacts',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getLuxSciContacts(
        search,
        page,
        limit
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch contacts'
      );
    }
  }
);

export const fetchBaskPatients = createAsyncThunk(
  'dashboard/fetchBaskPatients',
  async ({ search = '', page = 1, limit = 10, tag = 'all' }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskPatients(search, page, limit, tag);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Bask patients';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskGhlOpportunityLogs = createAsyncThunk(
  'dashboard/fetchBaskGhlOpportunityLogs',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskGhlOpportunityLogs(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch Bask GHL opportunity logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskOrders = createAsyncThunk(
  'dashboard/fetchBaskOrders',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskOrders(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Bask orders';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskOrderLogs = createAsyncThunk(
  'dashboard/fetchBaskOrderLogs',
  async ({ search = '', page = 1, limit = 10, action = '' }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskOrderLogs(search, page, limit, action);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Bask add-order logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskPaymentLogs = createAsyncThunk(
  'dashboard/fetchBaskPaymentLogs',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskPaymentLogs(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Bask payment logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskSmsLogs = createAsyncThunk(
  'dashboard/fetchBaskSmsLogs',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskSmsLogs(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch SMS logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskAbandonedSessionLogs = createAsyncThunk(
  'dashboard/fetchBaskAbandonedSessionLogs',
  async ({ search = '', page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskAbandonedSessionLogs(search, page, limit);
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch Bask abandoned session logs';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchBaskNorthbeamOrders = createAsyncThunk(
  'dashboard/fetchBaskNorthbeamOrders',
  async ({ search = '', page = 1, limit = 10, status = 'success' }, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getBaskNorthbeamOrders(
        search,
        page,
        limit,
        status,
      );
      return response.data.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch Northbeam orders';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    stats: {
      totalContactsLuxsci: 0,
      totalContactsGHL: 0,
      lastSync: null,
      status: 'Live',
    },
    apiLogs: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
    },
    baskPatients: [],
    baskPatientsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskGhlOpportunityLogs: [],
    baskGhlOpportunityLogsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskOrders: [],
    baskOrdersPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskOrderLogs: [],
    baskOrderLogsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskPaymentLogs: [],
    baskPaymentLogsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskSmsLogs: [],
    baskSmsLogsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskAbandonedSessionLogs: [],
    baskAbandonedSessionLogsPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskNorthbeamOrders: [],
    baskNorthbeamOrdersPagination: { page: 1, limit: 10, total: 0, pages: 0 },
    baskNorthbeamStats: {
      csvSuccess: 0,
      csvFailed: 0,
      csvRemaining: 0,
      webhookSuccess: 0,
      webhookFailed: 0,
      totalStored: 0,
    },
    contacts: [],
    contactsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
      matches_returned: 0,
    },
    loading: false,
    loadingStats: false,
    loadingLogs: false,
    loadingBaskPatients: false,
    loadingBaskGhlOpportunityLogs: false,
    loadingBaskOrders: false,
    loadingBaskOrderLogs: false,
    loadingBaskPaymentLogs: false,
    loadingBaskSmsLogs: false,
    loadingBaskAbandonedSessionLogs: false,
    loadingBaskNorthbeamOrders: false,
    error: null,
    searchQuery: '',
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Stats
      .addCase(fetchStats.pending, (state) => {
        state.loadingStats = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.loading = state.loadingLogs; // Keep loading true if logs are still loading
        state.stats = action.payload;
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.loadingStats = false;
        state.loading = state.loadingLogs; // Keep loading true if logs are still loading
        state.error = action.payload;
      })
      // Fetch API Logs
      .addCase(fetchApiLogs.pending, (state) => {
        state.loadingLogs = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApiLogs.fulfilled, (state, action) => {
        state.loadingLogs = false;
        state.loading = state.loadingStats; // Keep loading true if stats are still loading
        state.apiLogs = action.payload.logs;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchApiLogs.rejected, (state, action) => {
        state.loadingLogs = false;
        state.loading = state.loadingStats; // Keep loading true if stats are still loading
        state.error = action.payload;
      })
      // Sync Now

      // Fetch LuxSci Contacts
      .addCase(fetchLuxSciContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLuxSciContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.contacts;
        state.contactsPagination = action.payload.pagination;
      })
      .addCase(fetchLuxSciContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Bask Patients
      .addCase(fetchBaskPatients.pending, (state) => {
        state.loadingBaskPatients = true;
        state.error = null;
      })
      .addCase(fetchBaskPatients.fulfilled, (state, action) => {
        state.loadingBaskPatients = false;
        state.baskPatients = action.payload.patients;
        state.baskPatientsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskPatients.rejected, (state, action) => {
        state.loadingBaskPatients = false;
        state.error = action.payload;
      })
      // Fetch Bask GHL Opportunity Logs
      .addCase(fetchBaskGhlOpportunityLogs.pending, (state) => {
        state.loadingBaskGhlOpportunityLogs = true;
        state.error = null;
      })
      .addCase(fetchBaskGhlOpportunityLogs.fulfilled, (state, action) => {
        state.loadingBaskGhlOpportunityLogs = false;
        state.baskGhlOpportunityLogs = action.payload.logs;
        state.baskGhlOpportunityLogsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskGhlOpportunityLogs.rejected, (state, action) => {
        state.loadingBaskGhlOpportunityLogs = false;
        state.error = action.payload;
      })
      // Fetch Bask Orders (baskorders collection)
      .addCase(fetchBaskOrders.pending, (state) => {
        state.loadingBaskOrders = true;
        state.error = null;
      })
      .addCase(fetchBaskOrders.fulfilled, (state, action) => {
        state.loadingBaskOrders = false;
        state.baskOrders = action.payload.orders;
        state.baskOrdersPagination = action.payload.pagination;
      })
      .addCase(fetchBaskOrders.rejected, (state, action) => {
        state.loadingBaskOrders = false;
        state.error = action.payload;
      })
      // Fetch Bask order webhook logs
      .addCase(fetchBaskOrderLogs.pending, (state) => {
        state.loadingBaskOrderLogs = true;
        state.error = null;
      })
      .addCase(fetchBaskOrderLogs.fulfilled, (state, action) => {
        state.loadingBaskOrderLogs = false;
        state.baskOrderLogs = action.payload.logs;
        state.baskOrderLogsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskOrderLogs.rejected, (state, action) => {
        state.loadingBaskOrderLogs = false;
        state.error = action.payload;
      })
      // Fetch Bask Payment Logs
      .addCase(fetchBaskPaymentLogs.pending, (state) => {
        state.loadingBaskPaymentLogs = true;
        state.error = null;
      })
      .addCase(fetchBaskPaymentLogs.fulfilled, (state, action) => {
        state.loadingBaskPaymentLogs = false;
        state.baskPaymentLogs = action.payload.logs;
        state.baskPaymentLogsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskPaymentLogs.rejected, (state, action) => {
        state.loadingBaskPaymentLogs = false;
        state.error = action.payload;
      })
      // Fetch Bask SMS Logs
      .addCase(fetchBaskSmsLogs.pending, (state) => {
        state.loadingBaskSmsLogs = true;
        state.error = null;
      })
      .addCase(fetchBaskSmsLogs.fulfilled, (state, action) => {
        state.loadingBaskSmsLogs = false;
        state.baskSmsLogs = action.payload.logs;
        state.baskSmsLogsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskSmsLogs.rejected, (state, action) => {
        state.loadingBaskSmsLogs = false;
        state.error = action.payload;
      })
      // Fetch Bask Abandoned Session Logs
      .addCase(fetchBaskAbandonedSessionLogs.pending, (state) => {
        state.loadingBaskAbandonedSessionLogs = true;
        state.error = null;
      })
      .addCase(fetchBaskAbandonedSessionLogs.fulfilled, (state, action) => {
        state.loadingBaskAbandonedSessionLogs = false;
        state.baskAbandonedSessionLogs = action.payload.logs;
        state.baskAbandonedSessionLogsPagination = action.payload.pagination;
      })
      .addCase(fetchBaskAbandonedSessionLogs.rejected, (state, action) => {
        state.loadingBaskAbandonedSessionLogs = false;
        state.error = action.payload;
      })
      // Fetch Northbeam orders
      .addCase(fetchBaskNorthbeamOrders.pending, (state) => {
        state.loadingBaskNorthbeamOrders = true;
        state.error = null;
      })
      .addCase(fetchBaskNorthbeamOrders.fulfilled, (state, action) => {
        state.loadingBaskNorthbeamOrders = false;
        state.baskNorthbeamOrders = action.payload.orders;
        state.baskNorthbeamOrdersPagination = action.payload.pagination;
        state.baskNorthbeamStats = action.payload.stats;
      })
      .addCase(fetchBaskNorthbeamOrders.rejected, (state, action) => {
        state.loadingBaskNorthbeamOrders = false;
        state.error = action.payload;
      });
  },
});

export const { setSearchQuery, clearError } = dashboardSlice.actions;
export default dashboardSlice.reducer;

